import { useSyncExternalStore } from 'react';

import type { GateStatus } from './types';
import { readGateStatus, readGateValue, subscribeToGates } from './gate-store';

// The app-wide source of feature-gate values. It holds one module-level
// GateClient; every gate read in the app, React hook or plain function, goes
// through it. Whoever installs the client with setLiveGateClient decides where
// the values come from (the backend's GET /api/gates, a static offline table,
// a test fake), and this module neither knows nor cares.
//
// Reads are context-free: useSyncExternalStore over the module-held client
// works anywhere in the tree, with no provider needed above the caller.
//
// The client itself and the plain reads live in gate-store.ts, which has no
// React import; this module re-exports them and adds the hooks.

export { type GateClient, readGateStatus, readGateValue, setLiveGateClient } from './gate-store';

/**
 * Reactive gate read that works anywhere in the tree; re-renders on client
 * (re)registration and on every `values_updated`.
 */
export function useLiveGate(name: string): boolean {
    return useSyncExternalStore(
        subscribeToGates,
        () => readGateValue(name),
        () => false,
    );
}

/** Reactive {@link readGateStatus}; re-renders on client (re)registration and
 *  on every `values_updated`, which the client emits whenever its values or
 *  loading status change. */
export function useLiveGateStatus(name: string): GateStatus {
    return useSyncExternalStore(
        subscribeToGates,
        () => readGateStatus(name),
        () => 'pending' as const,
    );
}
